import { Box, Button, Card, CardContent, Stack, Typography, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import HistoryIcon from '@mui/icons-material/History';
import { useNavigate } from 'react-router-dom';

interface CompletionActionsProps {
  rfpName: string;
  proposalId?: string;
  onGenerateNew: () => void;
}

export default function CompletionActions({ rfpName, proposalId, onGenerateNew }: CompletionActionsProps) {
  const navigate = useNavigate();

  const handleDownload = () => {
    // Trigger download from ProposalViewer
    const downloadButton = document.querySelector('[data-download-button]') as HTMLButtonElement;
    if (downloadButton) {
      downloadButton.click();
    }
  };

  const handleViewHistory = () => {
    navigate('/history');
  };

  return (
    <Card
      elevation={3}
      sx={{
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        border: '2px solid',
        borderColor: 'success.light',
      }}
    >
      <CardContent>
        <Stack spacing={3} alignItems="center" textAlign="center">
          {/* Success Icon & Message */}
          <Box>
            <CheckCircleIcon
              sx={{
                fontSize: 64,
                color: 'success.main',
                mb: 2,
                animation: 'scaleIn 0.3s ease-out'
              }}
            />
            <Typography variant="h5" component="h2" gutterBottom fontWeight={600}>
              Proposal Generated Successfully!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Your proposal for <strong>{rfpName}</strong> is ready
            </Typography>
            {proposalId && (
              <Chip
                label={`ID: ${proposalId.slice(0, 8)}`}
                size="small"
                sx={{ mt: 1 }}
              />
            )}
          </Box>

          {/* Primary Action */}
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={onGenerateNew}
            fullWidth
            sx={{
              maxWidth: 400,
              py: 1.5,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              fontSize: '1.1rem',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #5a3a7a 100%)',
                transform: 'translateY(-2px)',
                boxShadow: 4,
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            Generate Another Proposal
          </Button>

          {/* Secondary Actions */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ width: '100%', maxWidth: 400 }}
          >
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              fullWidth
              sx={{
                '&:hover': {
                  backgroundColor: 'action.hover',
                }
              }}
            >
              Download
            </Button>
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={handleViewHistory}
              fullWidth
              sx={{
                '&:hover': {
                  backgroundColor: 'action.hover',
                }
              }}
            >
              View History
            </Button>
          </Stack>

          {/* Helper Text */}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
            Your proposal has been saved and can be accessed from the History page
          </Typography>
        </Stack>
      </CardContent>

      <style>
        {`
          @keyframes scaleIn {
            from {
              transform: scale(0);
              opacity: 0;
            }
            to {
              transform: scale(1);
              opacity: 1;
            }
          }
        `}
      </style>
    </Card>
  );
}
